/**
 * PC 拖拽文件进聊天窗口（12b/14.3b）：容器级 drag 事件接线，返回遮罩显隐状态。
 * 任意类型原样交回调（sendAttachments 按图片/视频/文件分流）。dragenter/leave
 * 在子元素间反复触发——用深度计数器防遮罩闪烁（遮罩本身也是一层子元素，
 * 不计数会 enter/leave 抵消成常灭）。仅桌面布局启用（enabled=false 全部
 * no-op，移动端无拖拽语义）。
 */
import { useCallback, useRef, useState } from 'preact/hooks';

function hasFiles(e: DragEvent): boolean {
  return e.dataTransfer !== null && Array.from(e.dataTransfer.types).includes('Files');
}

export interface ImageDropBind {
  onDragEnter(e: DragEvent): void;
  onDragOver(e: DragEvent): void;
  onDragLeave(e: DragEvent): void;
  onDrop(e: DragEvent): void;
}

export function useImageDrop(
  enabled: boolean,
  onFiles: (files: File[]) => void,
): { dragging: boolean; bind: ImageDropBind } {
  const [dragging, setDragging] = useState(false);
  const depthRef = useRef(0);

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (!enabled || !hasFiles(e)) return;
      e.preventDefault();
      depthRef.current += 1;
      setDragging(true);
    },
    [enabled],
  );

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!enabled || !hasFiles(e)) return;
      e.preventDefault(); // 不 preventDefault 浏览器会拦下 drop 自行打开文件
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    },
    [enabled],
  );

  const onDragLeave = useCallback(
    (_e: DragEvent) => {
      if (!enabled || depthRef.current === 0) return;
      depthRef.current -= 1;
      if (depthRef.current === 0) setDragging(false);
    },
    [enabled],
  );

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      depthRef.current = 0;
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) onFiles(files);
    },
    [enabled, onFiles],
  );

  return { dragging, bind: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
